import { normalizeUserError, userErrorMessage } from './utils/userErrors';
import { logger } from './utils/logger';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
if (!API_BASE_URL && process.env.NODE_ENV === 'production') {
  logger.error('REACT_APP_API_BASE_URL is not set');
}

// ─── Error extraction ──────────────────────────────────────────────────────────
/**
 * Converts any caught error into a clean, user-friendly string.
 *
 * Priority order:
 *  1. First field-level validation error  (errors.field[0])
 *  2. Top-level message from the API body (json.message)
 *  3. Generic HTTP status fallback
 *  4. Network / unknown error
 *
 * Never exposes raw status codes, JSON dumps, or technical details to the user.
 */
export function extractError(err: unknown, fallback = 'We could not complete this action. Please try again.'): string {
  return userErrorMessage(err, fallback);
}

// ─── Internal class that carries the parsed API body ─────────────────────────
class ApiError extends Error {
  apiBody: unknown;
  status: number;

  constructor(message: string, status: number, body?: unknown) {
    super(userErrorMessage({ status, apiBody: body as { message?: unknown }, message }));
    this.name = 'ApiError';
    this.status = status;
    this.apiBody = body;
  }
}

// ─── Response handler ──────────────────────────────────────────────────────────
async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    // 401 with an existing token means the session has expired.
    // Clear stored auth data and redirect to the login page so the
    // user is not silently left in a broken authenticated state.
    if (res.status === 401) {
      // Only treat a 401 as a session expiry when the user actually had a session.
      // Without this guard, public pages that call auth-required endpoints (e.g. /agents)
      // would redirect unauthenticated visitors to /login.
      const hadSession = !!localStorage.getItem('srs_user');
      // Clear any legacy localStorage tokens
      localStorage.removeItem('srs_token');
      localStorage.removeItem('srs_user');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      // The HTTP-only cookie is cleared by the backend on logout
      if (hadSession) {
        window.dispatchEvent(new CustomEvent('srs:session-expired'));
        throw new ApiError('Your session has expired. Please sign in again.', 401);
      }
      // No prior session — let the call fail normally so the page can handle it gracefully
      throw new ApiError('Please sign in to continue.', 401);
    }

    const text = await res.text().catch(() => '');

    // Maintenance mode: the API gates non-admin traffic with a 503 + { maintenance: true }.
    // Surface it as a global event so the app can show a full-page maintenance screen,
    // then halt this request (like the 401 session-expiry path) so callers don't error out.
    if (res.status === 503) {
      try {
        const body = JSON.parse(text);
        if (body?.maintenance) {
          window.dispatchEvent(new CustomEvent('srs:maintenance', {
            detail: { message: typeof body.message === 'string' ? body.message : undefined },
          }));
          return new Promise(() => {});
        }
      } catch { /* not a maintenance payload — fall through to normal handling */ }
    }

    // HTML error page (e.g. 500 from server misconfiguration)
    if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
      throw new ApiError('We could not complete this action. Please try again shortly.', res.status);
    }

    // Try to parse structured JSON error from API
    try {
      const json = JSON.parse(text);

      // Build a clean message: prefer first field error, then top-level message
      let cleanMessage = 'Something went wrong. Please try again.';
      if (json?.errors && typeof json.errors === 'object') {
        const firstField = Object.values(json.errors as Record<string, string[]>).flat()[0];
        if (typeof firstField === 'string' && firstField.trim()) {
          cleanMessage = firstField;
        }
      } else if (typeof json?.message === 'string' && json.message.trim()) {
        cleanMessage = json.message;
      } else if (typeof json?.error === 'string' && json.error.trim()) {
        cleanMessage = json.error;
      }

      // Map common HTTP statuses to friendly fallbacks if message is still generic
      if (cleanMessage === 'Something went wrong. Please try again.') {
        cleanMessage = httpStatusMessage(res.status);
      }

      throw new ApiError(cleanMessage, res.status, json);
    } catch (parseErr) {
      // parseErr could be the ApiError we just threw — re-throw it
      if (parseErr instanceof ApiError) throw parseErr;
      // Otherwise it's a JSON parse failure
      throw new ApiError(httpStatusMessage(res.status), res.status);
    }
  }

  if (res.status === 204) return {} as T;

  try {
    const text = await res.text();
    if (!text) return {} as T;
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError('Received an unexpected response format. Please try again.', 200);
  }
}

/** Maps HTTP status codes to short, user-friendly sentences. */
function httpStatusMessage(status: number): string {
  switch (status) {
    case 400: return 'The request was invalid. Please check your input.';
    case 401: return 'You are not logged in. Please sign in and try again.';
    case 403: return 'You do not have permission to perform this action.';
    case 404: return 'The requested resource was not found.';
    case 408: return 'The request timed out. Please check your connection and try again.';
    case 409: return 'This action conflicts with existing data. Please refresh and try again.';
    case 422: return 'Some of the information provided is invalid. Please check and try again.';
    case 429: return 'Too many attempts. Please wait a moment and try again.';
    case 500: return 'We could not complete this action. Please try again shortly.';
    case 502: return 'Service temporarily unavailable. Please try again in a moment.';
    case 503: return 'Service is currently unavailable. Please try again later.';
    default:  return 'Something went wrong. Please try again.';
  }
}

// ─── Silent token refresh ───────────────────────────────────────────────────────
// On a 401 for an authenticated user, transparently call /auth/refresh once and
// retry the original request. Concurrent 401s share a single in-flight refresh so
// we never fire multiple refreshes at once. Login/refresh endpoints are excluded
// (a 401 there is a real auth failure, not an expired session).
let refreshInFlight: Promise<boolean> | null = null;

function attemptRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), API_READ_TIMEOUT_MS);
    refreshInFlight = fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
    })
      .then((r) => r.ok)
      .catch(() => false);
    // Allow a fresh refresh on the next expiry once this one settles.
    refreshInFlight.finally(() => { clearTimeout(timer); refreshInFlight = null; });
  }
  return refreshInFlight;
}

const NO_REFRESH_PATHS = ['/auth/refresh', '/auth/login', '/auth/register'];

/** Collapse concurrent identical GETs (e.g. App + gate both hit /auth/me). */
function currentSessionScope(): string {
  try { return String(JSON.parse(localStorage.getItem('srs_user') ?? 'null')?.id ?? 'anonymous'); }
  catch { return 'anonymous'; }
}

const inflightGets = new Map<string, Promise<unknown>>();

// Release browser connections when a read stalls, including response-body reads.
// A UI-only Promise.race does not cancel fetch and can starve route chunk loads.
export const API_READ_TIMEOUT_MS = 20000;

/** Short-lived session cache for anonymous, non-user-specific GET responses. */
const SESSION_CACHE_PREFIX = 'srs_api_get_cache:';
const SESSION_CACHE_TTL_MS: Record<string, number> = {
  '/cms/settings': 5 * 60 * 1000,
};

function readSessionCache<T>(path: string): T | null {
  const ttl = SESSION_CACHE_TTL_MS[path];
  if (!ttl) return null;
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_PREFIX + path);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; body: T };
    if (Date.now() - parsed.at > ttl) {
      sessionStorage.removeItem(SESSION_CACHE_PREFIX + path);
      return null;
    }
    return parsed.body;
  } catch {
    return null;
  }
}

function writeSessionCache<T>(path: string, body: T): void {
  if (!SESSION_CACHE_TTL_MS[path]) return;
  try {
    sessionStorage.setItem(SESSION_CACHE_PREFIX + path, JSON.stringify({ at: Date.now(), body }));
  } catch {
    /* quota — ignore */
  }
}

async function send<T>(path: string, init: RequestInit): Promise<T> {
  if (init.method !== 'GET') return sendRequest<T>(path, init).catch(error => { throw normalizeUserError(error); });
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (init.signal?.aborted) controller.abort();
  else init.signal?.addEventListener('abort', abort, { once: true });
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, API_READ_TIMEOUT_MS);
  try {
    return await sendRequest<T>(path, { ...init, signal: controller.signal });
  } catch (error) {
    if (timedOut) throw new Error('The request timed out. Please try again.');
    throw normalizeUserError(error);
  } finally {
    clearTimeout(timer);
    init.signal?.removeEventListener('abort', abort);
  }
}

async function sendRequest<T>(path: string, init: RequestInit): Promise<T> {
  const requestScope = currentSessionScope();
  let res = await fetch(`${API_BASE_URL}${path}`, init);
  if (requestScope !== currentSessionScope()) throw new ApiError('Session changed. Please retry.', 401);

  if (
    res.status === 401 &&
    !NO_REFRESH_PATHS.some((p) => path.startsWith(p)) &&
    !!localStorage.getItem('srs_user')
  ) {
    const refreshed = await attemptRefresh();
    if (init.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (requestScope !== currentSessionScope()) throw new ApiError('Session changed. Please retry.', 401);
    if (refreshed) {
      // Retry the original request exactly once with the renewed cookie.
      res = await fetch(`${API_BASE_URL}${path}`, init);
    }
  }

  if (requestScope !== currentSessionScope()) throw new ApiError('Session changed. Please retry.', 401);
  return handleResponse<T>(res);
}

// ─── Public API functions ──────────────────────────────────────────────────────

// Auth is handled exclusively via the HTTP-only `srs_token` cookie set by the backend.
// The Authorization header is no longer sent from the client — JwtFromCookieMiddleware
// on the server injects it from the cookie before the auth guard runs.
function baseHeaders(extra?: HeadersInit): HeadersInit {
  return {
    'Content-Type': 'application/json',
    ...(extra ?? {}),
  };
}

export async function apiGet<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  const cached = readSessionCache<T>(path);
  if (cached !== null) return cached;

  const scope = currentSessionScope();
  const key = `${scope}:GET:${path}`;
  if (options?.signal) {
    const body = await send<T>(path, { ...options, method: 'GET', credentials: 'include', headers: baseHeaders(options.headers) });
    if (currentSessionScope() !== scope) throw new ApiError('Session changed. Please retry.', 401);
    writeSessionCache(path, body);
    return body;
  }
  const existing = inflightGets.get(key);
  if (existing) return existing as Promise<T>;

  const promise = send<T>(path, {
    ...options,
    method: 'GET',
    credentials: 'include',
    headers: baseHeaders(options?.headers),
  }).then((body) => {
    if (currentSessionScope() !== scope) throw new ApiError('Session changed. Please retry.', 401);
    writeSessionCache(path, body);
    return body;
  }).finally(() => {
    inflightGets.delete(key);
  });

  inflightGets.set(key, promise);
  return promise;
}

export async function apiPost<T = unknown, B = unknown>(
  path: string,
  body: B,
  options?: RequestInit,
): Promise<T> {
  return send<T>(path, {
    ...options,
    method: options?.method ?? 'POST',
    credentials: 'include',
    headers: baseHeaders(options?.headers),
    body: JSON.stringify(body),
  });
}

export async function apiPut<T = unknown, B = unknown>(
  path: string,
  body: B,
  options?: RequestInit,
): Promise<T> {
  return send<T>(path, {
    ...options,
    method: 'PUT',
    credentials: 'include',
    headers: baseHeaders(options?.headers),
    body: JSON.stringify(body),
  });
}

export async function apiPostFormData<T = unknown>(path: string, formData: FormData): Promise<T> {
  return send<T>(path, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    // No Content-Type header — browser sets multipart/form-data with boundary automatically
  });
}

export async function apiDelete<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  return send<T>(path, {
    ...options,
    method: 'DELETE',
    credentials: 'include',
    headers: baseHeaders(options?.headers),
  });
}
